import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { toast } from "react-hot-toast";
import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import { FaRegSadCry } from "react-icons/fa";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

export default function AdminDashboard() {
  const [selectedDuration, setSelectedDuration] = useState("7");
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [perDayTotals, setPerDayTotals] = useState({});
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {};
        if (selectedDuration === "today") {
          const today = new Date().toISOString().split("T")[0];
          params.start_date = today;
          params.end_date = today;
        } else {
          params.duration = selectedDuration;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/dashboard/trails?`,
          {
            params,
            headers: {
              "access-token": localStorage.getItem("accessToken") || "",
            },
          }
        );

        const apiData = response.data.data;
        const formattedData = {
          labels: apiData.labels,
          datasets: apiData.datasets.map((dataset) => {
            let borderColor, backgroundColor;
            switch (dataset.label) {
              case "SPCAD_ACAD_Beta":
                borderColor = "#962A2A";
                backgroundColor = "rgba(150, 42, 42, 0.2)";
                break;
              case "SPCAD_ZWCAD":
                borderColor = "#3296FF";
                backgroundColor = "rgba(50, 150, 255, 0.2)";
                break;
              case "SPCAD_CADMATE":
                borderColor = "#035223";
                backgroundColor = "rgba(3, 82, 35, 0.2)";
                break;
              case "SPCAD_GSTARCAD":
                borderColor = "#4F4FB3";
                backgroundColor = "rgba(79, 79, 179, 0.2)";
                break;
              case "SPCAD_BRX":
                borderColor = "#0A3E7E";
                backgroundColor = "rgba(10, 62, 126, 0.2)";
                break;
              case "SPCAD_PCAD":
                borderColor = "#DEF186";
                backgroundColor = "rgba(223, 241, 134, 0.2)";
                break;
              default:
                borderColor = "#333";
                backgroundColor = "rgba(51, 51, 51, 0.2)";
            }

            return {
              label: dataset.label,
              data: dataset.data,
              borderColor,
              backgroundColor,
              borderWidth: 2,
              fill: true,
              pointRadius: 2,
              tension: 0.5,
              pointBackgroundColor: borderColor,
            };
          }),
        };
        const totals = {};
        let totalUsersCount = 0;

        apiData.datasets.forEach((dataset) => {
          const total = dataset.data.reduce((sum, value) => sum + value, 0);
          totals[dataset.label] = total;
          totalUsersCount += total;
        });

        setChartData(formattedData);
        setPerDayTotals(totals);
        setTotalUsers(totalUsersCount);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        if (err.response.data?.message) {
          toast.error(err.response.data.message)
        } else {
          console.log(err)
          toast.error("Something Went Wrong")
        }
        // console.error("Error fetching chart data:", err);
        setError(err.response.data.message);
      }
    };

    fetchData();
  }, [selectedDuration]);

  return (
    <div style={{ fontFamily: "Arial, sans-serif" }}>
      <AdminDashboardLayout />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">
            <div className="d-flex flex-row justify-content-between mt-2">
              <div>
                <h2>Dashboard</h2>
                <p style={{ textAlign: "left", color: "#555" }}>
                  Analyze user trial statistics and trends over time. Select a duration to view specific data.
                </p>
              </div>
            </div>
            {/* Chart Section */}
            <div>
              {loading ? (
                <div style={{ textAlign: "center", fontSize: "18px", color: "#333" }}>Loading data...</div>
              ) : error ? (
                <div style={{ textAlign: "center", color: "red" }}>{error}</div>
              ) : chartData.labels.length === 1 ? (
                <div style={{ textAlign: "center", color: "#666", fontSize: "18px", marginTop: "30px" }}>
                  <FaRegSadCry style={{ fontSize: "30px", color: "#ff6b6b", marginBottom: "10px" }} />
                  <p>No data to display for the selected duration.</p>
                </div>
              ) : (
                <>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        alignItems: "center",
                        marginBottom: "20px",
                        gap: "10px",
                      }}
                    >
                      <label htmlFor="duration" style={{ color: "#333", fontWeight: "bold" }}>
                        Select Duration:
                      </label>
                      <select
                        id="duration"
                        value={selectedDuration}
                        onChange={(e) => setSelectedDuration(e.target.value)}
                        style={{
                          padding: "10px",
                          borderRadius: "5px",
                          border: "1px solid #ccc",
                          fontSize: "14px",
                          color: "#333",
                          backgroundColor: "#fff",
                        }}
                      >
                        <option value="1">Today</option>
                        <option value="3">Last 3 Days</option>
                        <option value="7">Last 7 Days</option>
                        <option value="30">Last 1 Month</option>
                        <option value="60">Last 2 Months</option>
                        <option value="90">Last 3 Months</option>
                        <option value="180">Last 6 Months</option>
                        <option value="365">Last 1 Year</option>
                        <option value="730">Last 2 Years</option>
                      </select>
                    </div>
                  </div>
                  {/* Per Day Totals Card */}
                  <h4>Trial User Statistics Over Time</h4>
                  <div
                    style={{
                      maxWidth: "500px",
                      // marginTop: "30px",
                      padding: "20px",
                      backgroundColor: "#fff",
                      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                      borderRadius: "8px",
                    }}
                  >
                    <h4 style={{ color: "#333" }}>
                      {selectedDuration === "today"
                        ? "Today's Total:"
                        : `${selectedDuration} Day${selectedDuration > 1 ? "s" : ""} Total: ${totalUsers}`}
                    </h4>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0, color: "#444" }}>
                      {Object.entries(perDayTotals).map(([product, count]) => (
                        <li key={product} style={{ marginBottom: "2px", fontSize: "14px" }}>
                          {product}: <strong>{count}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          position: "top",
                          labels: {
                            color: "#444",
                            font: { family: "Roboto, Arial, sans-serif", size: 14, weight: "500" },
                            padding: 20,
                            usePointStyle: true,
                          },
                        },
                        // title: {
                        //   display: true,
                        //   text: "Trial User Statistics Over Time",
                        //   color: "#222",
                        //   font: { family: "Roboto, Arial, sans-serif", size: 20, weight: "bold" },
                        //   padding: { top: 20, bottom: 10 },
                        // },
                        tooltip: {
                          backgroundColor: "rgba(0, 0, 0, 0.8)",
                          titleFont: { family: "Roboto, Arial, sans-serif", size: 14, weight: "bold" },
                          bodyFont: { family: "Roboto, Arial, sans-serif", size: 12 },
                          footerFont: { family: "Roboto, Arial, sans-serif", size: 10, style: "italic" },
                          boxPadding: 10,
                          callbacks: {
                            label: function (tooltipItem) {
                              return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: "Date",
                            color: "#444",
                            font: { family: "Roboto, Arial, sans-serif", size: 16, weight: "500" },
                          },
                          ticks: {
                            color: "#666",
                            font: { family: "Roboto, Arial, sans-serif", size: 12 },
                          },
                          grid: {
                            color: "rgba(0, 0, 0, 0.1)",
                          },
                        },
                        y: {
                          title: {
                            display: true,
                            text: "Values",
                            color: "#444",
                            font: { family: "Roboto, Arial, sans-serif", size: 16, weight: "500" },
                          },
                          ticks: {
                            color: "#666",
                            font: { family: "Roboto, Arial, sans-serif", size: 12 },
                            stepSize: 1,
                          },
                          grid: {
                            color: "rgba(0, 0, 0, 0.1)",
                          },
                          min: 0,
                          max: Math.max(...chartData.datasets.flatMap(dataset => dataset.data)) + 2,
                        },
                      },
                      elements: {
                        point: {
                          radius: 0,
                        },
                      },
                    }}
                  />


                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}